/**
 * TranslatableWrapper - Generic wrapper for any data type with translation capabilities.
 * Replaces the domain-specific TranslatableEvent with a generic solution.
 */
import type {
	TranslatableField,
	TranslationConfig,
	FieldConfig,
	TranslationRequest,
	TranslationResult,
	TranslationStatus,
} from './types';

/**
 * Internal structure for managing preprocessed fields.
 */
interface ProcessedField {
	config: FieldConfig<any>;
	field: TranslatableField;
	/** If preprocessed, stores the individual parts */
	parts?: TranslatableField[];
}

/**
 * TranslatableWrapper wraps any data type with translation capabilities.
 * Generic replacement for TranslatableEvent.
 *
 * @template T - The source data type being wrapped
 *
 * @example
 * ```typescript
 * const wrapper = new TranslatableWrapper(event, eventConfig);
 * const requests = wrapper.collectForTranslation('search-results');
 * // ... translate via service ...
 * wrapper.applyTranslations(results, 'search-results');
 * const name = wrapper.getField('name', isTranslated);
 * ```
 */
export class TranslatableWrapper<T> {
	/** Map of field key to processed field data */
	private fieldsMap = new Map<string, ProcessedField>();

	/**
	 * Create a new translatable wrapper.
	 *
	 * @param source - The original data object to wrap
	 * @param config - Translation configuration for this data type
	 */
	constructor(
		public readonly source: T,
		private readonly config: TranslationConfig<T>,
	) {
		this.initializeFields();
	}

	/**
	 * Initialize translatable fields from source data.
	 */
	private initializeFields(): void {
		this.config.fields.forEach((fieldConfig) => {
			const value = fieldConfig.extract(this.source);

			// Only process non-null/undefined/empty values
			if (value !== null && value !== undefined && value !== '') {
				const processedField: ProcessedField = {
					config: fieldConfig,
					field: {
						original: value,
						translated: null,
						status: 'pending',
					},
				};

				// Handle preprocessing (e.g., splitting multi-line text)
				if (fieldConfig.preprocess) {
					const parts = fieldConfig.preprocess(value);
					processedField.parts = parts.map((part) => ({
						original: part,
						translated: null,
						status: 'pending' as TranslationStatus,
					}));
				}

				this.fieldsMap.set(fieldConfig.key, processedField);
			}
		});
	}

	/**
	 * Get the unique ID of the wrapped source item.
	 */
	getId(): string | number {
		return this.config.getId(this.source);
	}

	/**
	 * Get the configuration name.
	 */
	getConfigName(): string {
		return this.config.name;
	}

	/**
	 * Check if a field exists in this wrapper.
	 */
	hasField(key: string): boolean {
		return this.fieldsMap.has(key);
	}

	/**
	 * Get a field's current display value.
	 *
	 * @param key - Field key (e.g., 'name', 'address')
	 * @param useTranslation - If true, return translated value (if available)
	 * @returns The appropriate value, or empty string if field doesn't exist
	 */
	getField(key: string, useTranslation: boolean): string {
		const processed = this.fieldsMap.get(key);
		if (!processed) {
			return '';
		}

		const { field, parts, config } = processed;

		// If preprocessed, reconstruct from parts
		if (parts && config.postprocess) {
			if (useTranslation && parts.every((p) => p.translated !== null)) {
				return config.postprocess(parts.map((p) => p.translated!));
			}
			return field.original;
		}

		// Simple field
		if (useTranslation && field.translated !== null) {
			return field.translated;
		}
		return field.original;
	}

	/**
	 * Get the original value of a field.
	 */
	getOriginal(key: string): string {
		const processed = this.fieldsMap.get(key);
		return processed?.field.original ?? '';
	}

	/**
	 * Get the translated value of a field (null if not translated).
	 */
	getTranslated(key: string): string | null {
		const processed = this.fieldsMap.get(key);
		if (!processed) {
			return null;
		}

		const { parts, config, field } = processed;

		// If preprocessed, reconstruct from parts
		if (parts && config.postprocess) {
			if (parts.every((p) => p.translated !== null)) {
				return config.postprocess(parts.map((p) => p.translated!));
			}
			return null;
		}

		return field.translated;
	}

	/**
	 * Get the translation status of a field.
	 */
	getFieldStatus(key: string): TranslationStatus | null {
		const processed = this.fieldsMap.get(key);
		if (!processed) {
			return null;
		}

		// If preprocessed, return lowest status (pending > translating > translated)
		if (processed.parts) {
			const statuses = processed.parts.map((p) => p.status);
			if (statuses.includes('error')) return 'error';
			if (statuses.includes('pending')) return 'pending';
			if (statuses.includes('translating')) return 'translating';
			return 'translated';
		}

		return processed.field.status;
	}

	/**
	 * Collect all untranslated fields for batch translation.
	 *
	 * @param context - Context string for key generation (e.g., 'search-results', 'event-view')
	 * @returns Array of translation requests
	 */
	collectForTranslation(context: string): TranslationRequest[] {
		const requests: TranslationRequest[] = [];
		const id = this.getId();

		Array.from(this.fieldsMap.entries()).forEach(([key, processed]) => {
			// Only process non-translated fields
			if (processed.field.status !== 'translated') {
				// Handle preprocessed fields (e.g., multi-line descriptions)
				if (processed.parts) {
					processed.parts.forEach((part, index) => {
						if (part.status === 'pending' && part.original.trim() !== '') {
							requests.push({
								key: `${context}__${id}__${key}__${index}`,
								text: part.original,
							});
							part.status = 'translating';
						}
					});
				} else if (processed.field.status === 'pending') {
					// Simple field
					requests.push({
						key: `${context}__${id}__${key}`,
						text: processed.field.original,
					});
					processed.field.status = 'translating';
				}
			}
		});

		return requests;
	}

	/**
	 * Apply translations from results.
	 *
	 * @param results - Array of translation results
	 * @param context - Context string (must match collectForTranslation)
	 */
	applyTranslations(results: TranslationResult[], context: string): void {
		const id = this.getId();
		const resultMap = new Map(results.map((r) => [r.key, r]));

		for (const [key, processed] of this.fieldsMap) {
			// Handle preprocessed fields
			if (processed.parts) {
				let allTranslated = true;
				processed.parts.forEach((part, index) => {
					const resultKey = `${context}__${id}__${key}__${index}`;
					const result = resultMap.get(resultKey);
					if (result) {
						part.translated = result.translated;
						part.status = 'translated';
					} else if (part.status !== 'translated') {
						allTranslated = false;
					}
				});

				// Update main field status
				if (allTranslated && processed.config.postprocess) {
					const translatedParts = processed.parts.map((p) => p.translated!);
					processed.field.translated = processed.config.postprocess(translatedParts);
					processed.field.status = 'translated';
				}
			} else {
				// Simple field
				const resultKey = `${context}__${id}__${key}`;
				const result = resultMap.get(resultKey);
				if (result) {
					processed.field.translated = result.translated;
					processed.field.status = 'translated';
				}
			}
		}
	}

	/**
	 * Reset all fields to pending status.
	 * Useful when source data changes or re-translation is needed.
	 */
	reset(): void {
		for (const processed of this.fieldsMap.values()) {
			processed.field.translated = null;
			processed.field.status = 'pending';

			if (processed.parts) {
				processed.parts.forEach((part) => {
					part.translated = null;
					part.status = 'pending';
				});
			}
		}
	}

	/**
	 * Check if all fields are fully translated.
	 */
	get isFullyTranslated(): boolean {
		for (const processed of this.fieldsMap.values()) {
			if (processed.field.status !== 'translated') {
				return false;
			}
		}
		return true;
	}

	/**
	 * Check if any field is currently being translated.
	 */
	get isTranslating(): boolean {
		for (const processed of this.fieldsMap.values()) {
			if (processed.field.status === 'translating') {
				return true;
			}
			if (processed.parts?.some((p) => p.status === 'translating')) {
				return true;
			}
		}
		return false;
	}

	/**
	 * Check if any field has an error.
	 */
	get hasError(): boolean {
		for (const processed of this.fieldsMap.values()) {
			if (processed.field.status === 'error') {
				return true;
			}
			if (processed.parts?.some((p) => p.status === 'error')) {
				return true;
			}
		}
		return false;
	}

	/**
	 * Get all field keys in this wrapper.
	 */
	getFieldKeys(): string[] {
		return Array.from(this.fieldsMap.keys());
	}

	/**
	 * Create a plain object with translated or original values.
	 * Useful for passing to components that expect simple props.
	 *
	 * @param useTranslation - If true, use translated values where available
	 * @returns Object with field keys and values
	 */
	toObject(useTranslation: boolean): Record<string, string> {
		const result: Record<string, string> = {};
		for (const key of this.fieldsMap.keys()) {
			result[key] = this.getField(key, useTranslation);
		}
		return result;
	}
}

/**
 * Factory function to create multiple wrappers from an array.
 *
 * @param items - Array of source items
 * @param config - Translation configuration
 * @returns Array of TranslatableWrapper instances
 */
export function createTranslatableWrappers<T>(
	items: T[],
	config: TranslationConfig<T>,
): TranslatableWrapper<T>[] {
	return items.map((item) => new TranslatableWrapper(item, config));
}
